import { useCallback, useEffect, useState } from 'react'

/**
 * The active workspace (PRD v1.1 §A): 'investment' (the existing PM
 * surfaces) or 'control' (the compliance workspace: rules, monitoring,
 * coverage). Persisted per browser; a custom event keeps every subscriber
 * (sidebar, pages) in sync without prop drilling.
 */
const STORAGE_KEY = 'acclimetry_workspace'
const EVENT = 'acclimetry:workspace-change'

export const WORKSPACES = {
  INVESTMENT: 'investment',
  CONTROL: 'control'
}

export const readWorkspace = () => {
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored === WORKSPACES.CONTROL ? WORKSPACES.CONTROL : WORKSPACES.INVESTMENT
}

export const useWorkspace = () => {
  const [workspace, setWorkspaceState] = useState(readWorkspace)

  useEffect(() => {
    const handler = () => setWorkspaceState(readWorkspace())
    window.addEventListener(EVENT, handler)
    return () => window.removeEventListener(EVENT, handler)
  }, [])

  const setWorkspace = useCallback((next) => {
    localStorage.setItem(STORAGE_KEY, next)
    window.dispatchEvent(new CustomEvent(EVENT))
  }, [])

  return { workspace, setWorkspace }
}

/** Roles that open the Control workspace (PRD §5.3 personas). */
const CONTROL_ROLES = [
  'organisation_administrator',
  'policy_owner',
  'rule_author',
  'rule_approver',
  'compliance_reviewer',
  'waiver_approver',
  'attestor',
  'auditor',
  // Capability wave (3 Oct 2026, R-01): the data roles' own work — data feeds, ingest, data
  // quality, reference governance, monitoring — is in this workspace, and every one of its
  // URLs sent them back to Portfolios. Which of its screens they see is their capabilities'.
  'data_operator',
  'instrument_data_steward'
]

/** Roles that open the Investment workspace. */
const INVESTMENT_ROLES = [
  'organisation_administrator',
  'investment_professional',
  'data_operator',
  'instrument_data_steward'
]

export const canUseControlWorkspace = (roles = []) =>
  roles.some((role) => CONTROL_ROLES.includes(role))

export const canUseInvestmentWorkspace = (roles = []) =>
  // An empty role list means the organisation context has not loaded yet —
  // the existing surfaces stay reachable rather than flashing away. (Since the
  // capability wave the sidebar and the home route wait for the context instead
  // of reading an empty list as Investment, R-10.)
  roles.length === 0 || roles.some((role) => INVESTMENT_ROLES.includes(role))

/**
 * Capability wave (3 Oct 2026) · What each workspace screen reads, as its API gate states it
 * (`canView(...)` in the backend's routes): a member is offered a screen — by the menu and by
 * the router — only when their capabilities let them view it. A screen not listed has no
 * capability gate: the Investment surfaces (gated by workspace), the account pages, and AI
 * governance, which stands outside the §14 table.
 */
export const ROUTE_CAPABILITIES = [
  { path: '/compliance', exact: true, capabilities: ['run_post_trade_monitoring'] },
  { path: '/compliance/documents', capabilities: ['upload_policy_documents', 'certify_document_review'] },
  { path: '/compliance/mandates', capabilities: ['upload_policy_documents', 'certify_document_review'] },
  { path: '/policies', capabilities: ['upload_policy_documents', 'certify_document_review'] },
  { path: '/compliance/rules', capabilities: ['draft_rules', 'approve_rules'] },
  { path: '/compliance/monitoring', capabilities: ['run_post_trade_monitoring'] },
  { path: '/compliance/exposure', capabilities: ['run_post_trade_monitoring'] },
  { path: '/compliance/trace', capabilities: ['upload_policy_documents', 'draft_rules', 'run_post_trade_monitoring', 'review_cases'] },
  { path: '/compliance/trade-impact', capabilities: ['submit_pre_trade_request', 'review_cases'] },
  { path: '/compliance/data-quality', capabilities: ['manage_integrations_and_mappings', 'approve_data_overrides', 'review_cases'] },
  { path: '/compliance/data-feeds', capabilities: ['manage_integrations_and_mappings'] },
  { path: '/compliance/ingest', capabilities: ['manage_integrations_and_mappings'] },
  { path: '/compliance/entities', capabilities: ['manage_hierarchy'] },
  { path: '/compliance/fx', capabilities: ['manage_integrations_and_mappings', 'approve_data_overrides'] },
  { path: '/compliance/reference', capabilities: ['approve_data_overrides', 'manage_integrations_and_mappings'] },
  { path: '/compliance/instruments', capabilities: ['approve_data_overrides'] },
  { path: '/compliance/cases', capabilities: ['review_cases', 'approve_waivers'] },
  { path: '/compliance/attestations', capabilities: ['complete_attestations', 'review_cases'] },
  { path: '/compliance/reports', capabilities: ['generate_reports'] },
  { path: '/compliance/audit', capabilities: ['export_audit_history'] }
]

/** The capabilities a screen needs to be viewed (any one of them), or null when it has no gate. */
export const capabilitiesForPath = (path = '') => {
  const clean = String(path).split('?')[0].replace(/\/+$/, '') || '/'
  const entry = ROUTE_CAPABILITIES.find((row) => (row.exact
    ? clean === row.path
    : clean === row.path || clean.startsWith(`${row.path}/`)))
  return entry ? entry.capabilities : null
}

const viewsAny = (capabilities, keys) => keys.some((key) => Boolean(capabilities?.[key]?.view))

/**
 * May this member open this screen? Its workspace first (the persona model: an investment
 * professional's Control reading stays the manager's view on their own pages), then what the
 * screen reads. `capabilities` absent (an older API) leaves the decision to the workspace.
 */
export const canOpenPath = (path, roles = [], capabilities = null) => {
  const workspace = workspaceOfPath(path)
  if (workspace !== 'shared' && !mayEnterWorkspace(roles, workspace)) return false
  const needs = capabilitiesForPath(path)
  if (!needs || !capabilities || !Object.keys(capabilities).length) return true
  return viewsAny(capabilities, needs)
}

/** Control screens in menu order — where a member who cannot use Portfolios lands (R-08). */
const CONTROL_HOME_ORDER = [
  '/compliance', '/compliance/cases', '/compliance/attestations', '/compliance/documents', '/compliance/rules',
  '/compliance/monitoring', '/compliance/data-quality', '/compliance/data-feeds', '/compliance/reference',
  '/compliance/instruments', '/compliance/reports', '/compliance/audit'
]

/**
 * W61 (§3.11 #11 · WSP-001) · Which workspace a route belongs to, so the
 * router can refuse a URL typed by a member who holds no role in it. The
 * workspace preference above decides which navigation renders; it was never
 * an access control, and a manager could reach every /compliance/* URL by
 * typing it. Shared routes (the policy author — gated server-side since W57
 * — account pages and file views) belong to both. So does Trade Impact: both
 * navigations link to it, §14 gives "Submit pre-trade request" to the
 * investment professional, an Investment role, and the API gates every
 * action on the page by capability — classed as Control, the guard sent the
 * very member the check is for back to Portfolios.
 */
export const workspaceOfPath = (path = '') => {
  if (path.startsWith('/compliance/trade-impact')) return 'shared'
  if (path.startsWith('/compliance')) return WORKSPACES.CONTROL
  if (path.startsWith('/policies') || path.startsWith('/account') || path.startsWith('/view/file') || path.startsWith('/admin')) return 'shared'
  return WORKSPACES.INVESTMENT
}

/**
 * The workspace a member lands in: the one they hold roles for — and, in Control, the first
 * screen they can use. An attestor used to land on the Compliance dashboard, which reads
 * monitoring they may not see, and met three refusal toasts on every sign-in (R-08).
 */
export const homePathFor = (roles = [], capabilities = null) => {
  if (canUseInvestmentWorkspace(roles)) return '/'
  if (!canUseControlWorkspace(roles)) return '/account/profile'
  if (!capabilities || !Object.keys(capabilities).length) return '/compliance'
  return CONTROL_HOME_ORDER.find((path) => canOpenPath(path, roles, capabilities)) || '/account/profile'
}

/** May this member open a route in the given workspace? */
export const mayEnterWorkspace = (roles = [], workspace) => {
  if (workspace === 'shared') return true
  return workspace === WORKSPACES.CONTROL ? canUseControlWorkspace(roles) : canUseInvestmentWorkspace(roles)
}
